<template>
  <v-dialog :value="value" max-width="1100" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="d-flex align-start justify-space-between">
        <div>
          <span class="pw-section d-block">Atenciones · {{ clinic ? clinic.clinicName : '' }}</span>
          <span class="pw-pct">{{ rangeText }}</span>
        </div>
        <v-btn icon small @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <div v-if="error" class="state-msg pa-6 text-center">
          <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
          No se pudieron cargar las atenciones de la clínica.
        </div>

        <v-data-table
          v-else
          :headers="headers"
          :items="items"
          :loading="loading"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100], itemsPerPageText: 'Filas por página' }"
          item-key="attendanceId"
          disable-sort
          no-data-text="Sin atenciones en el período seleccionado"
          loading-text="Cargando atenciones…"
          dense
        >
          <template v-slot:item.usedAt="{ item }">{{ formatDateTime(item.usedAt) }}</template>

          <template v-slot:item.pet="{ item }">
            <v-icon small class="mr-1">{{ item.petSpecies === 'cat' ? 'mdi-cat' : 'mdi-dog' }}</v-icon>
            {{ item.petName || '—' }}
          </template>

          <template v-slot:item.tutor="{ item }">{{ fullName(item.tutorName, item.tutorSurname) }}</template>
          <template v-slot:item.vet="{ item }">{{ fullName(item.vetName, item.vetSurname) }}</template>

          <template v-slot:item.services="{ item }">
            <span class="services">{{ item.services.length ? item.services.join(', ') : '—' }}</span>
          </template>

          <template v-slot:item.invoiceStatus="{ item }">
            <v-chip x-small outlined :color="statusOf(item).color">{{ statusOf(item).label }}</v-chip>
          </template>

          <template v-slot:item.pawerCoverage="{ item }">{{ item.pawerCoverage | clp }}</template>
          <template v-slot:item.userPayment="{ item }">{{ item.userPayment | clp }}</template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import PetmedMetricsService from '@/services/petmed-metrics.service';

const DETAIL_HEADERS = [
  { text: 'Fecha', value: 'usedAt' },
  { text: 'Mascota', value: 'pet' },
  { text: 'Tutor', value: 'tutor' },
  { text: 'Veterinario', value: 'vet' },
  { text: 'Servicios', value: 'services' },
  { text: 'Estado pago', value: 'invoiceStatus' },
];

const COST_HEADERS = [
  { text: 'Cobertura', value: 'pawerCoverage', align: 'end' },
  { text: 'Pago usuario', value: 'userPayment', align: 'end' },
];

const INVOICE_STATUS = {
  paid: { label: 'Pagado', color: 'success' },
  pending: { label: 'Pendiente', color: 'warning' },
};

export default {
  name: 'PetmedClinicAttendancesDialog',

  props: {
    value: { type: Boolean, default: false },
    // Fila de insights.byClinic ({ clinicId, clinicName, ... }).
    clinic: { type: Object, default: null },
    fromDate: { type: String, default: null },
    toDate: { type: String, default: null },
    // Sin `petmed-metrics:view-costs` los montos llegan en null: se ocultan sus columnas.
    showCosts: { type: Boolean, default: true },
  },

  data() {
    return {
      items: [],
      total: 0,
      loading: false,
      error: false,
      options: { page: 1, itemsPerPage: 10 },
      requestId: 0,
    };
  },

  computed: {
    headers() {
      return this.showCosts ? [...DETAIL_HEADERS, ...COST_HEADERS] : DETAIL_HEADERS;
    },
    rangeText() {
      if (!this.fromDate && !this.toDate) return 'Histórico';
      const fmt = (d) => (d ? moment(d).format('DD/MM/YYYY') : '…');
      return `${fmt(this.fromDate)} – ${fmt(this.toDate)}`;
    },
  },

  watch: {
    // Cada apertura parte desde la página 1; si ya estaba en 1, el watcher de
    // options no dispara y se pide explícitamente.
    value(open) {
      if (!open) return;
      this.items = [];
      this.total = 0;
      if (this.options.page === 1) this.fetch();
      else this.options = { ...this.options, page: 1 };
    },
    options: {
      deep: true,
      handler() {
        if (this.value) this.fetch();
      },
    },
  },

  methods: {
    // Igual que en MetricasPetmed: una respuesta de un pedido anterior se descarta.
    async fetch() {
      if (!this.clinic) return;
      const requestId = ++this.requestId;
      this.loading = true;
      const res = await PetmedMetricsService.getAttendances({
        clinicId: this.clinic.clinicId,
        fromDate: this.fromDate,
        toDate: this.toDate,
        page: this.options.page,
        pageSize: this.options.itemsPerPage,
      });
      if (requestId !== this.requestId) return;
      if (res.status === 'ok') {
        this.items = res.data;
        this.total = res.meta ? res.meta.total : res.data.length;
        this.error = false;
      } else {
        this.items = [];
        this.total = 0;
        this.error = true;
      }
      this.loading = false;
    },
    formatDateTime(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';
    },
    fullName(name, surname) {
      return [name, surname].filter(Boolean).join(' ') || '—';
    },
    statusOf(item) {
      return INVOICE_STATUS[item.invoiceStatus] || { label: item.invoiceStatus, color: 'grey' };
    },
  },
};
</script>

<style scoped>
.state-msg {
  font-size: 13px;
  color: var(--pw-text-muted);
}
.services {
  font-size: 12px;
  display: inline-block;
  max-width: 220px;
}
</style>
